import { Input } from "@progress/kendo-react-inputs";
import { PanelBar, PanelBarItem, Splitter } from "@progress/kendo-react-layout";
import { Fragment, useState } from "react";
import { useSelector } from "react-redux";
import { List } from "../../../components/list";
import {
  Button,
  DropDownButton,
  Toolbar,
  ToolbarItem,
  ToolbarSeparator,
} from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import { DatasetViewPageForm } from "./datasetviewpageform";
import { Form } from "../../../components/form";
import {
  setEntityFilter,
  setEntitySysFilter,
} from "../../../redux/slices/datasetviewpageslice";
import { useDispatch } from "react-redux";
import {
  canAccessDataSet,
  canCreateDataset,
  canDeleteDataset,
  canEditDataset,
  canViewData,
  datasetMaxReached,
} from "../../../routing/authorizationhandlers";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

const toolbarRenderer = (props) => {
  const { currentTheme, currentUser, pageState, t } = props;

  const toolsVisibleIcon = pageState.toolsVisible ? "unpin" : "pin";
  const toolsVisibleTitle = pageState.toolsVisible
    ? t("datasets.datasetview.hide")
    : t("datasets.datasetview.show");

  const saveTitle = t("datasets.datasetview.update");

  return (
    <Toolbar>
      <ToolbarItem>
        <Button
          disabled={pageState.processing}
          style={{ color: currentTheme.primary }}
          icon={toolsVisibleIcon}
          onClick={props.onToolsToggle}
        >
          {toolsVisibleTitle}
        </Button>
      </ToolbarItem>
      <ToolbarSeparator />
      {currentUser.IsAuthenticated && canCreateDataset(currentUser) && (
        <ToolbarItem>
          <Button
            disabled={pageState.processing || datasetMaxReached(currentUser)}
            style={{ color: currentTheme.primary }}
            icon="plus"
            onClick={props.onNew}
          >
            {t("datasets.datasetview.create")}
          </Button>
        </ToolbarItem>
      )}
      {((pageState.mode == 1 &&
        currentUser.IsAuthenticated &&
        canCreateDataset(currentUser)) ||
        (pageState.mode == 2 &&
          currentUser.IsAuthenticated &&
          canEditDataset(currentUser))) && (
        <Fragment>
          <ToolbarSeparator />
          <ToolbarItem>
            <Button
              disabled={
                pageState.processing ||
                (pageState.mode == 1 && datasetMaxReached(currentUser))
              }
              style={{ color: currentTheme.primary }}
              icon="save"
              onClick={props.onSave}
            >
              {saveTitle}
            </Button>
          </ToolbarItem>
        </Fragment>
      )}

      {pageState.mode == 2 &&
        currentUser.IsAuthenticated &&
        canDeleteDataset(currentUser) &&
        pageState.formValues.Modules == 0 && (
          <Fragment>
            <ToolbarSeparator />
            <ToolbarItem>
              <Button
                disabled={pageState.processing}
                style={{ color: currentTheme.primary }}
                icon="delete"
                onClick={(e) => props.onDelete(pageState.formValues.Id)}
              >
                {t("datasets.datasetview.delete")}
              </Button>
            </ToolbarItem>
          </Fragment>
        )}

      {pageState.mode == 2 &&
        pageState.formValues.SourceTypeId != 2 &&
        currentUser.IsAuthenticated &&
        canEditDataset(currentUser) && (
          <Fragment>
            <ToolbarSeparator />
            <ToolbarItem>
              <Button
                disabled={pageState.processing}
                style={{ color: currentTheme.primary }}
                icon="edit"
                onClick={props.onTestConnection}
              >
                {t("datasets.datasetview.test")}
              </Button>
            </ToolbarItem>
          </Fragment>
        )}
      {currentUser.IsAuthenticated && canViewData(currentUser) && (
        <Fragment>
          <ToolbarSeparator />
          <ToolbarItem>
            <Button
              disabled={pageState.processing}
              style={{ color: currentTheme.primary }}
              icon="data"
              onClick={props.onDataCorrection}
            >
              {t("datasets.datasetview.correction")}
            </Button>
          </ToolbarItem>
        </Fragment>
      )}
    </Toolbar>
  );
};

const getEntityOperations = (entity, currentUser, t) => {
  let result = [];

  if (
    currentUser.IsAuthenticated &&
    (canAccessDataSet(currentUser) || canEditDataset(currentUser))
  )
    result.push({ id: 2, text: t("datasets.datasetview.view"), icon: "eye" });

  if (
    currentUser.IsAuthenticated &&
    entity.Modules == 0 &&
    canDeleteDataset(currentUser)
  )
    result.push({
      id: 3,
      text: t("datasets.datasetview.delete2"),
      icon: "delete",
    });

  return result;
};

const entityMenuItemRenderer =
  (currentTheme, entity, onItemClicked) => (props) => {
    let isDisabled = false;
    if (
      props.item.id == 3 &&
      (entity.IsFederationRoot || entity?.IsRootSubscription)
    )
      isDisabled = true;

    if (props.item.text)
      return (
        <div
          style={{ display: "flex", width: "100%", padding: "0px 6px" }}
          onClick={(e) => onItemClicked(entity.Id, props.item)}
        >
          <span
            className={`k-icon k-i-${props.item.icon}`}
            style={{ display: "inline-block" }}
          ></span>
          <div style={{ marginLeft: 12 }}>{props.item.text}</div>
        </div>
      );
    else return <div className="menu-separator" style={{}} />;
  };

const entitiesItemRenderer =
  (currentTheme, currentUser, system, t) => (props, item, index) => {
    let icon = "k-i-data-sql";
    if (item.Modules == 0) icon = "k-i-data";
    if (system && item.Modules != 0) {
      return (
        <div
          key={item.Id}
          className={`list-item`}
          style={{
            color: currentTheme.bodyText,
          }}
          onDoubleClick={(e) => props.onSystemEntityDoubleClick(item.Id)}
        >
          <div className={`list-item-icon k-icon ${icon} `}></div>
          <div className="list-item-title" style={{ fontWeight: `700` }}>
            {item.Title}
            {"©"}
          </div>
          {currentUser.IsAuthenticated &&
            (canAccessDataSet(currentUser) ||
              canEditDataset(currentUser) ||
              canDeleteDataset(currentUser)) && (
              <div className="list-item-operations">
                <DropDownButton
                  className="buttons-container-button"
                  items={getEntityOperations(item, currentUser, t)}
                  itemRender={entityMenuItemRenderer(
                    currentTheme,
                    item,
                    (entityId, _item) => {
                      if (_item.id == 1 || _item.id == 2) {
                        //view
                        props.onSystemEntitySelected(entityId, _item);
                      } else if (_item.id == 3)
                        // delete
                        props.onDelete(entityId, _item);
                    },
                  )}
                  icon="more-horizontal"
                  fillMode={"flat"}
                />
              </div>
            )}
        </div>
      );
    } else if (!system && item.Modules == 0) {
      return (
        <div
          key={item.Id}
          className={`list-item`}
          style={{
            color: currentTheme.bodyText,
          }}
          onDoubleClick={(e) => props.onEntityDoubleClick(item.Id)}
        >
          <div className={`list-item-icon k-icon ${icon}`}></div>
          <div className="list-item-title" style={{ fontWeight: `400` }}>
            {item.Title}
          </div>
          {currentUser.IsAuthenticated &&
            (canAccessDataSet(currentUser) ||
              canEditDataset(currentUser) ||
              canDeleteDataset(currentUser)) && (
              <div className="list-item-operations">
                <DropDownButton
                  className="buttons-container-button"
                  items={getEntityOperations(item, currentUser, t)}
                  itemRender={entityMenuItemRenderer(
                    currentTheme,
                    item,
                    (entityId, _item) => {
                      if (_item.id == 1 || _item.id == 2) {
                        //view
                        props.onEntitySelected(entityId, _item);
                      } else if (_item.id == 3)
                        // delete
                        props.onDelete(entityId, _item);
                    },
                  )}
                  icon="more-horizontal"
                  fillMode={"flat"}
                />
              </div>
            )}
        </div>
      );
    }
  };

export const DatasetViewPageBody = (props) => {
  const {
    onSplitterChange,
    onNew,
    onSave,
    onActivateDeactivate,
    onDelete,
    onTestConnection,
    onDataCorrection,
    onClose,
    onErrorbarClose,
    onEntitySelected,
    onEntityDoubleClick,
    onSystemEntitySelected,
    onSystemEntityDoubleClick,
    updateDirtyState,
    onToolsToggle,
    ...other
  } = props;

  const currentTheme = useSelector((state) => state.currentTheme);
  const currentUser = useSelector((state) => state.currentUser);
  const pageState = useSelector((state) => state.datasetViewPage);
  const appearance = useSelector((state) => state.appearance);
  const dispatch = useDispatch();
  const entities = pageState.entities;
  const { t } = useTranslation();
  const canShowToolbar =
    currentUser.IsAuthenticated &&
    (canCreateDataset(currentUser) ||
      (pageState.mode == 2 && canEditDataset(currentUser)) ||
      (pageState.mode == 2 &&
        pageState.formValues.Modules == 0 &&
        canDeleteDataset(currentUser)) ||
      canViewData(currentUser));

  const getPanelBarItems = () => {
    const panelBarItems = [];
    panelBarItems.push(
      <PanelBarItem
        className="panel-user-defined-entities"
        key="userentities"
        title={t("datasets.datasetview.user_defined")}
        expanded={true}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Input
            type="search"
            value={pageState.entityFilter}
            onChange={(e) => dispatch(setEntityFilter(e.value))}
            style={{
              width: "calc(100% - 16px)",
              margin: "8px 8px 8px 8px",
              backgroundColor: "transparent",
            }}
            placeholder={t("datasets.datasetview.search")}
          />
          <List
            className="entities"
            items={entities.filter((e) => {
              if (e.Modules === 0) {
                if (
                  !pageState.entityFilter ||
                  pageState.entityFilter?.trim() == ""
                )
                  return true;

                const filter = pageState.entityFilter?.toLowerCase();
                return e?.Title?.toLowerCase().indexOf(filter) >= 0;
              }
            })}
            itemRenderer={entitiesItemRenderer(
              currentTheme,
              currentUser,
              false,
              t,
            )}
            onEntitySelected={props.onEntitySelected}
            onEntityDoubleClick={props.onEntityDoubleClick}
            onSystemEntitySelected={props.onSystemEntitySelected}
            onSystemEntityDoubleClick={props.onSystemEntityDoubleClick}
            onDelete={props.onDelete}
          />
        </div>
      </PanelBarItem>,
    );
    panelBarItems.push(
      <PanelBarItem
        className="panel-system-entities"
        key="systementities"
        title={t("datasets.datasetview.system")}
        expanded={true}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Input
            type="search"
            value={pageState.entitySysFilter}
            onChange={(e) => dispatch(setEntitySysFilter(e.value))}
            style={{
              width: "calc(100% - 16px)",
              margin: "8px 8px 8px 8px",
              backgroundColor: "transparent",
            }}
            placeholder={t("datasets.datasetview.search")}
          />
          <List
            className="entities"
            items={entities.filter((e) => {
              if (e.Modules !== 0) {
                if (
                  !pageState.entitySysFilter ||
                  pageState.entitySysFilter?.trim() == ""
                )
                  return true;

                const filter = pageState.entitySysFilter?.toLowerCase();
                return e?.Title?.toLowerCase().indexOf(filter) >= 0;
              }
            })}
            itemRenderer={entitiesItemRenderer(
              currentTheme,
              currentUser,
              true,
              t,
            )}
            onEntitySelected={props.onEntitySelected}
            onEntityDoubleClick={props.onEntityDoubleClick}
            onSystemEntitySelected={props.onSystemEntitySelected}
            onSystemEntityDoubleClick={props.onSystemEntityDoubleClick}
            onDelete={props.onDelete}
          />
        </div>
      </PanelBarItem>,
    );

    return panelBarItems;
  };

  return (
    <Fragment>
      {pageState.processing && (
        <div className="page-loader">
          <Loader
            size={"large"}
            type="infinite-spinner"
            themeColor={"primary"}
          />
          {pageState.processingText && (
            <div style={{ fontSize: 13, color: currentTheme.primary }}>
              {pageState.processingText}
            </div>
          )}
        </div>
      )}
      {pageState.pageLoaded && (
        <Fragment>
          <Splitter
            style={{ border: "none" }}
            panes={pageState.panes}
            onChange={onSplitterChange}
          >
            <div className="datasetsview-page-manager">
              <PanelBar style={{ border: "none" }} expanded={[".0", ".1"]}>
                {getPanelBarItems()}
              </PanelBar>
            </div>
            <div className={`right datasetview-page-wrapper`}>
              <div className="datasetview-mobile-close">
            <Button
              icon={appearance.isRightToLeft ? "arrow-right" : "arrow-left"}
              fillMode="flat"
              title={t("currentuser.back")}
              aria-label={t("currentuser.back")}
              onClick={onClose}
            >
              {t("currentuser.back")}
            </Button>
              </div>
              <Form
                className="datasetview-page-form"
                currentTheme={currentTheme}
                currentUser={currentUser}
                toolbarRenderer={toolbarRenderer}
                hideToolbar={!canShowToolbar}
                t={t}
                onNew={onNew}
                onToolsToggle={onToolsToggle}
                onSave={onSave}
                onDataCorrection={onDataCorrection}
                onTestConnection={onTestConnection}
                onActivateDeactivate={onActivateDeactivate}
                onDelete={onDelete}
                errorMessages={pageState.formServerErrors}
                onErrorbarClose={onErrorbarClose}
                pageState={pageState}
              >
                {pageState.mode > 0 ? (
                  <DatasetViewPageForm updateDirtyState={updateDirtyState} />
                ) : (
                  <Fragment></Fragment>
                )}
              </Form>
            </div>
          </Splitter>
        </Fragment>
      )}
      <style>
        {`
                   .datasetsview-page > .content
                   {
                   } 
   
                    .datasetsview-page .k-splitter{
                      flex-direction:${
                        appearance.isRightToLeft ? "row-reverse" : "row"
                      } 
                    }
                      
                    .datasetsview-page .k-splitter .k-pane{
                      direction:${appearance.isRightToLeft ? "rtl" : "ltr"} 
                    }

                    .datasetsview-page .datasetview-page-wrapper{
                    border: ${currentTheme.pageFormBorder};   
                    height:100%; direction:${
                      appearance.isRightToLeft ? "rtl" : "ltr"
                    } 
                    
                    }   
                   
                    /*the manager*/
                    .datasetsview-page > .content .datasetsview-page-manager
                    {
                      border: ${currentTheme.managerBorder};
                      border-radius: ${currentTheme.pageBorderRadius};
                      background-color: ${currentTheme.componentBgFaded};
                    }

                    .datasetsview-page > .content .datasetsview-page-manager .k-dropdownlist
                    {
                      /*bg of datasets dropdown*/
                      background-color: transparent;
                      color: ${currentTheme.componentText};
                    }

                    .datasetsview-page > .content .datasetsview-page-manager .k-dropdownlist:focus
                    {
                        box-shadow: none;   
                    }

                    .datasetsview-page > .content .datasetsview-page-manager .panel-entities .k-input::placeholder
                    {
                        color: ${currentTheme.componentText};
                        /*font-style: italic;*/
                    }

                    .datasetsview-page > .content .datasetsview-page-manager .k-panelbar
                    {
                      background-color: transparent;
                    }

                    .datasetsview-page > .content .expressions-page-manager ul li.k-panelbar-header.k-panelbar-item .k-link .k-icon
                    {
                      /*the arrow near every panel header*/
                      color: ${currentTheme.touchColor}!important;
                      margin-right: 6px;
                    }

                    .datasetsview-page .datasetsview-page-manager ul li.k-panelbar-header.k-panelbar-item .k-link
                    {
                      /*the span in the above <li> i.e. the title of every group in the manager: datasets, datasetsview, columns and description*/  
                      background-color: transparent;
                      color: ${currentTheme.touchColor};
                      border-bottom: 1px solid ${currentTheme.separatorColor}; 
                    }

                    .datasetsview-page .datasetsview-page-manager ul li.k-panelbar-header.k-panelbar-item .k-link:hover
                    {
                      /*the above hovered - do nothing*/
                    }

                    .datasetsview-page .datasetsview-page-manager .datasetsview .list-item:hover
                    {
                      /*expression item hover */
                      background-color: ${currentTheme.componentBorderColor};
                      cursor: pointer;
                    }

                    .datasetsview-page > .content .datasetsview-page-manager .dataset,
                    .datasetsview-page > .content .datasetsview-page-manager .description,
                    .datasetsview-page > .content .datasetsview-page-manager .k-child-animation-container
                    {
                      /*bg color of exp. manager elements */
                    }

                    .datasetsview-page > .content .datasetsview-page-manager .list-container
                    {
                      /*bg of both the exp. and columns Lists*/
                      background-color: transparent;
                      padding: 0;
                    }

                    .datasetsview-page > .content .datasetsview-page-manager .k-panelbar-header
                    {
                      background-color: transparent;
                    }

                    .datasetsview-page > .content .datasetsview-page-manager .k-panelbar-content
                    {
                      background-color: transparent;
                    }

                    .datasetsview-page > .content .datasetsview-page-manager .datasetsview .list-item-datasource-icon
                    {
                      /*first icon in datasetsview list*/
                      padding-left: 12px!important;
                    }

                    .datasetsview-page > .content .datasetsview-page-manager .datasetsview .list-item-datasource-icon
                    {
                      /*second icon in data sources list*/
                      padding: 5px 3px;                 
                    }

                    .datasetsview-page > .content .datasetsview-page-manager .datasetsview .list-item-title
                    {
                      vertical-align: middle;/*fine tuning*/
                    }               


                    /*the form*/
                    .datasetsview-page-form
                    {
                      background-color: transparent;
                      padding-left: 0px;
                      border: ${currentTheme.pageFormBorder};   
                      border-radius: ${currentTheme.pageBorderRadius};
                    }   

                    /*form toolbar*/
                    .datasetsview-page-form > .toolbar .k-toolbar
                    {
                        width: 100%;
                        border-left: none !important;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                        border-top: none !important;
                        border-right: none !important;
                        background-color: ${
                          currentTheme.toolbarBg
                        };/*transparent;*/
                    }                    

                    .datasetsview-page > .content .toolbar .k-button
                    {
                      /*the toolbar buttons*/
                      border: none;
                      padding: 6px 8px;
                    }

                    .datasetsview-page > .content .toolbar .k-button-text
                    {
                      /*color of the text in every toolbar button=black in azure theme*/
                      color: ${currentTheme.toolbarText};
                    }

                    .datasetsview-page > .content .toolbar .k-button-icon
                    {
                      font-weight: 500;
                      color: ${currentTheme.primary};
                    }

                    .datasetsview-page > .content .form-root .content
                    {
                      background-color: ${currentTheme.pageContentBg};
                    }

                    /*styling the popup menu */
                    .k-popup ul{
                        padding: 0px;
                    }

                    .k-popup ul > li{
                        margin: 0;
                        padding: 0px;                                
                    }

                    .k-popup ul > li > span{
                        border: none !important;
                        box-shadow: none !important;
                    }

                    .k-menu-link{
                        padding: 0px 0px !important;                        
                    }

                    .k-tabstrip-items .k-link {
                        color:${currentTheme.primary}
                    }

                    .k-tabstrip-items .k-item.k-active .k-link {
                        color:${currentTheme.touchColor}
                    }
                    .k-panelbar > .k-item > .k-link:focus{
                        box-shadow:none;
                    }

                    .datasetsview-page > .content .datasetsview-page-manager .k-panelbar-header{
                        border-color:transparent;
                         box-shadow:none;
                    }

                     .k-panelbar > .k-item > .k-link,.k-panelbar > .k-item > .k-link.k-selected:hover, .k-panelbar > .k-panelbar-header > .k-link.k-selected:hover{
                         box-shadow:none;
                    }

                    .datasetsview-page .datasetview-mobile-close {
                      display: none;
                    }

                    @media (max-width: 1024px) {
                      .datasetsview-page > .content {
                        overflow-y: auto;
                      }

                      .datasetsview-page > .content .k-splitbar,
                      .datasetsview-page > .content .k-splitter > .k-pane:first-child {
                        display: none !important;
                      }

                      .datasetsview-page > .content .k-splitter > .k-pane:last-child {
                        flex: 1 1 100% !important;
                        width: 100% !important;
                        max-width: 100% !important;
                      }

                      .datasetsview-page .datasetview-page-wrapper,
                      .datasetsview-page .datasetview-page-form {
                        height: auto;
                        min-height: 100%;
                      }

                      .datasetsview-page .datasetview-page-form > .toolbar {
                        display: none;
                      }

                      .datasetsview-page .datasetview-mobile-close {
                        display: flex;
                        justify-content: ${
                          appearance.isRightToLeft ? "flex-end" : "flex-start"
                        };
                        padding: 0 8px 8px;
                      }
                    }

                  `}
      </style>
    </Fragment>
  );
};
